import { type MutableRefObject, useCallback, useEffect, useRef } from 'react';

import {
  cancelStickyElementScroll,
  isElementFullyVisibleWithinContainer,
  startStickyElementScroll,
} from '@/components/ui/scroll/stickyElementScroll';
import { onChatSendFromAnyWindow } from '@/features/chat/chat.events';
import { useInViewport } from '@/hooks/useInViewport';

export const BOTTOM_SCROLL_TRACKING_ANCHOR_ID = 'bottom-scroll-tracking-anchor';

export const CHAT_THREAD_ID = 'chat-thread';

/** How close to the bottom counts as "reading the tail" when the viewport shrinks. */
const VIEWPORT_SHRINK_STICK_THRESHOLD_PX = 24;

const SCROLL_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
]);

const USER_SCROLL_EVENTS = ['wheel', 'touchmove'] as const;

/** During a live turn, the scroll-to-bottom button hides while the streaming tip is in view; clicking it lands past the bottom padding for breathing room. */
export function useChatScroll(
  threadId: string,
  isLive: boolean,
  userHasScrolledRef: MutableRefObject<boolean>
) {
  const { ref: streamingTipInViewportRef, inViewport: streamingTipInViewport } =
    useInViewport<HTMLDivElement>();
  const { ref: bottomAnchorInViewportRef, inViewport: bottomAnchorInViewport } =
    useInViewport<HTMLDivElement>();

  const scrollOffset = useRef<number>(0);

  const showScrollToBottom = isLive
    ? !streamingTipInViewport
    : !bottomAnchorInViewport;

  const rawScrollToBottom = useScrollToBottom(threadId);

  // Wrap scrollToBottom to also reset scroll intent so auto-follow resumes.
  // Uses a stable ref callback (not debounced) — rawScrollToBottom is already
  // debounced, so intent reset happens immediately on click.
  const scrollToBottomRef = useRef(rawScrollToBottom);
  scrollToBottomRef.current = rawScrollToBottom;

  const scrollToBottom = useRef((smooth = true, onScrollEnd?: () => void) => {
    userHasScrolledRef.current = false;
    scrollToBottomRef.current(smooth, onScrollEnd);
  }).current;

  // Reset scroll intent on thread switch so a previous thread's
  // scroll-up doesn't suppress auto-scroll in the new thread
  useEffect(() => {
    userHasScrolledRef.current = false;
  }, [threadId, userHasScrolledRef]);

  useEffect(() => {
    const scrollContainer = document.getElementById(
      `${CHAT_THREAD_ID}-${threadId}`
    );
    if (!scrollContainer) return;

    const markUserScrolled = () => {
      userHasScrolledRef.current = true;
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (SCROLL_KEYS.has(event.key)) markUserScrolled();
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (event.target === scrollContainer) markUserScrolled();
    };

    for (const eventName of USER_SCROLL_EVENTS) {
      scrollContainer.addEventListener(eventName, markUserScrolled, {
        passive: true,
      });
    }
    scrollContainer.addEventListener('keydown', handleKeyDown);
    scrollContainer.addEventListener('pointerdown', handlePointerDown, {
      passive: true,
    });

    return () => {
      for (const eventName of USER_SCROLL_EVENTS) {
        scrollContainer.removeEventListener(eventName, markUserScrolled);
      }
      scrollContainer.removeEventListener('keydown', handleKeyDown);
      scrollContainer.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [threadId, userHasScrolledRef]);

  // Keep the bottom of the thread in view when the viewport itself shrinks -
  // the terminal dock opening below, the window resizing. The browser keeps
  // scrollTop fixed on resize, so the tail of the conversation slides under
  // the new bottom edge and reads as "the dock covered my text". Only when
  // the reader was at the bottom: mid-thread, the browser's behaviour (top
  // of the viewport stays put) is the right one. Scroll-height changes from
  // new content are the VList's business, so only the viewport is observed.
  useEffect(() => {
    const scrollContainer = document.getElementById(
      `${CHAT_THREAD_ID}-${threadId}`
    );
    if (!scrollContainer || typeof ResizeObserver === 'undefined') return;

    let lastHeight = scrollContainer.clientHeight;
    const observer = new ResizeObserver(() => {
      const height = scrollContainer.clientHeight;
      const shrunkBy = lastHeight - height;
      // Distance from the bottom as it was *before* this shrink: the browser
      // has already applied the new height, so add the lost pixels back.
      const wasFromBottom =
        scrollContainer.scrollHeight -
        scrollContainer.scrollTop -
        height -
        Math.max(shrunkBy, 0);
      lastHeight = height;
      if (shrunkBy <= 0 || wasFromBottom > VIEWPORT_SHRINK_STICK_THRESHOLD_PX) {
        return;
      }
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    });
    observer.observe(scrollContainer);

    return () => observer.disconnect();
  }, [threadId]);

  // Reset scroll intent when the user sends a new message
  useEffect(() => {
    const listener = onChatSendFromAnyWindow((sentThreadId) => {
      if (sentThreadId === threadId) {
        userHasScrolledRef.current = false;
      }
    });

    return () => {
      listener.unsubscribe();
    };
  }, [threadId, userHasScrolledRef]);

  return {
    streamingTipInViewportRef,
    bottomAnchorInViewportRef,
    showScrollToBottom,
    scrollToBottom,
    scrollOffset,
  };
}

function useScrollToBottom(threadId: string) {
  const followUpScrollRafId = useRef<number | null>(null);

  useEffect(() => () => cancelStickyElementScroll(followUpScrollRafId), []);

  return useCallback(
    (smooth = true, onScrollEnd?: () => void) => {
      const scrollContainer = document.getElementById(
        `${CHAT_THREAD_ID}-${threadId}`
      );
      const bottomAnchor = scrollContainer?.querySelector(
        `#${BOTTOM_SCROLL_TRACKING_ANCHOR_ID}`
      ) as HTMLElement | null;
      if (!scrollContainer || !bottomAnchor) {
        onScrollEnd?.();
        return;
      }

      startStickyElementScroll({
        rafIdRef: followUpScrollRafId,
        scrollContainer,
        targetElement: bottomAnchor,
        scrollIntoViewOptions: {
          block: 'end',
          behavior: smooth ? 'smooth' : 'auto',
        },
        isTargetReached: ({
          scrollContainer: currentScrollContainer,
          targetElement,
          epsilonPx,
        }) => {
          const isAtBottom =
            currentScrollContainer.scrollHeight -
              currentScrollContainer.clientHeight -
              currentScrollContainer.scrollTop <=
            epsilonPx;
          return (
            isAtBottom ||
            isElementFullyVisibleWithinContainer(
              currentScrollContainer,
              targetElement,
              epsilonPx
            )
          );
        },
        onFinish: onScrollEnd,
      });
    },
    [threadId]
  );
}
